'use client';

/**
 * LiquidGlassHighlight Component
 *
 * A reusable overlay that provides Apple Liquid Glass specular highlight effect.
 * Renders a `::after`-style gradient overlay at the top edge of its parent,
 * simulating light reflection on a glass surface.
 *
 * Usage: Wrap any glass surface with this component or use the `sx` helper.
 *
 * @see Apple WWDC 2025 — Liquid Glass design language
 */

import Box from '@mui/material/Box';
import { type SxProps, styled, type Theme } from '@mui/material/styles';
import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface LiquidGlassHighlightProps {
  /**
   * Highlight intensity (opacity of the specular gradient).
   * @default 'regular'
   */
  intensity?: 'subtle' | 'regular' | 'strong';

  /**
   * Border radius to match the parent container.
   * @default 16
   */
  borderRadius?: number | string;

  /**
   * Position of the highlight.
   * @default 'top'
   */
  position?: 'top' | 'bottom';

  /**
   * Additional sx props for customization.
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Constants
// ============================================================================

const intensityMap = {
  subtle: { light: 0.3, dark: 0.08 },
  regular: { light: 0.6, dark: 0.15 },
  strong: { light: 0.8, dark: 0.25 },
} as const;

// ============================================================================
// Styled Component
// ============================================================================

interface StyledHighlightProps {
  $intensity: 'subtle' | 'regular' | 'strong';
  $borderRadius: number | string;
  $position: 'top' | 'bottom';
}

const StyledHighlight = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledHighlightProps>(({ theme, $intensity, $borderRadius, $position }) => {
  const isDark = theme.palette.mode === 'dark';
  const opacity = isDark ? intensityMap[$intensity].dark : intensityMap[$intensity].light;

  const isTop = $position === 'top';
  const gradient = isTop
    ? `linear-gradient(to bottom, rgba(255, 255, 255, ${opacity}), rgba(255, 255, 255, 0))`
    : `linear-gradient(to top, rgba(255, 255, 255, ${opacity * 0.5}), rgba(255, 255, 255, 0))`;

  return {
    position: 'absolute',
    [isTop ? 'top' : 'bottom']: 0,
    left: 0,
    right: 0,
    height: isTop ? '50%' : '30%',
    maxHeight: isTop ? 120 : 80,
    background: gradient,
    borderRadius: $borderRadius,
    pointerEvents: 'none',
    zIndex: 1,
  };
});

// ============================================================================
// Component
// ============================================================================

export const LiquidGlassHighlight: React.FC<LiquidGlassHighlightProps> = ({
  intensity = 'regular',
  borderRadius = 16,
  position = 'top',
  sx,
}) => {
  return (
    <StyledHighlight
      $intensity={intensity}
      $borderRadius={borderRadius}
      $position={position}
      aria-hidden="true"
      sx={sx}
    />
  );
};

// ============================================================================
// SX Helper
// ============================================================================

/**
 * Returns an `sx` object with `::after` pseudo-element for specular highlight.
 * Apply this to any glass surface's `sx` prop to add the highlight effect
 * without adding a DOM element.
 *
 * @example
 * <Box sx={{ ...liquidGlassHighlightSx({ borderRadius: 16 }) }}>
 *   content
 * </Box>
 */
export function liquidGlassHighlightSx(options?: {
  intensity?: 'subtle' | 'regular' | 'strong';
  borderRadius?: number | string;
  isDark?: boolean;
}): SxProps<Theme> {
  const { intensity = 'regular', borderRadius = 16, isDark } = options ?? {};

  return {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '50%',
      maxHeight: 120,
      background: (theme: Theme) => {
        const dark = isDark ?? theme.palette.mode === 'dark';
        const opacity = dark ? intensityMap[intensity].dark : intensityMap[intensity].light;
        return `linear-gradient(to bottom, rgba(255, 255, 255, ${opacity}), rgba(255, 255, 255, 0))`;
      },
      borderRadius,
      pointerEvents: 'none',
      zIndex: 1,
    },
  };
}

export default LiquidGlassHighlight;
